import {
  emailCodeResponseSchema,
  pendingEmailCodeSchema,
  type EmailCodeResponse,
} from "@go-novus/admin-api";
import { widgetBusinessFetch } from "./business-fetch";
import { z } from "zod";

/** 登录入口来自 Liquid，回跳当前店面页面。 */
export function storefrontLoginUrl(
  loginUrl: string,
  location: Location = window.location,
): string {
  const url = new URL(loginUrl, location.origin);
  url.searchParams.set(
    "return_to",
    `${location.pathname}${location.search}${location.hash}`,
  );
  return url.href;
}

export const customerAuthSchema = z.object({
  store_login: z.enum(["signed_in", "signed_out", "unknown"]),
  email_code: z.enum(["available", "verified", "unavailable"]),
  pending_email_code: pendingEmailCodeSchema.optional(),
  login_hint_email: z.string().optional(),
});
export type CustomerAuth = z.infer<typeof customerAuthSchema>;

// —— App Proxy 接口 ——————————————————————————————————————————

function proxyUrl(handshakeUrl: string, subpath: string): string {
  const url = new URL(handshakeUrl, window.location.href);
  url.pathname = url.pathname.replace(
    /\/handshake$/,
    `/customer-auth/${subpath}`,
  );
  return url.href;
}

const statusResponseSchema = z.object({ auth: customerAuthSchema });
export type CustomerAuthStatusResult =
  { status: "ok"; auth: CustomerAuth } | { status: "failed"; code?: string };

async function postAuth(
  handshakeUrl: string,
  subpath: "status" | "select" | "email",
  body: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<CustomerAuthStatusResult> {
  try {
    const response = await fetchImpl(proxyUrl(handshakeUrl, subpath), {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(8_000),
    });
    const json: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const code = z.object({ error: z.string() }).safeParse(json);
      return {
        status: "failed",
        code: code.success ? code.data.error : undefined,
      };
    }
    const parsed = statusResponseSchema.safeParse(json);
    if (!parsed.success) return { status: "failed" };
    return {
      status: "ok",
      auth: parsed.data.auth,
    };
  } catch {
    return { status: "failed" };
  }
}

/**
 * 店铺登录同步:带上本页 Liquid 认出的顾客和当前会话,服务端据此
 * 记录确定登录或退出观测，并续接该 uid 的待验证请求。
 */
export function readCustomerAuthStatus(
  handshakeUrl: string,
  input: {
    uid: string;
    conversationId?: string;
    liquidCustomerId?: string;
  },
  fetchImpl: typeof fetch = widgetBusinessFetch(handshakeUrl),
): Promise<CustomerAuthStatusResult> {
  return postAuth(
    handshakeUrl,
    "status",
    {
      uid: input.uid,
      ...(input.conversationId === undefined
        ? {}
        : { conversation_id: input.conversationId }),
      ...(input.liquidCustomerId === undefined
        ? {}
        : { liquid_customer_id: input.liquidCustomerId }),
    },
    fetchImpl,
  );
}

/** Playground 以商家指定的店内顾客身份测查单;顾客按邮箱精确匹配。 */
export function selectPlaygroundCustomer(
  handshakeUrl: string,
  input: { uid: string; email: string; conversationId?: string },
  fetchImpl: typeof fetch = widgetBusinessFetch(handshakeUrl),
): Promise<CustomerAuthStatusResult> {
  return postAuth(
    handshakeUrl,
    "select",
    {
      uid: input.uid,
      email: input.email,
      ...(input.conversationId === undefined
        ? {}
        : { conversation_id: input.conversationId }),
    },
    fetchImpl,
  );
}

export type EmailVerificationAction = {
  operation: "send" | "verify" | "playground";
  email: string;
  code?: string;
  challenge_id?: string;
};
export type VerifyEmailAction = (
  input: EmailVerificationAction,
) => Promise<EmailCodeResponse>;
export const emailCodeFailure: EmailCodeResponse = {
  status: "failed",
  retry_after: 0,
  tries_left: 0,
};
/** 邮箱与验证码只在此 POST 请求体传输，不经过聊天通道。 */
export async function requestEmailCode(
  handshakeUrl: string,
  input: { uid: string; conversationId?: string } & EmailVerificationAction,
  fetchImpl: typeof fetch = widgetBusinessFetch(handshakeUrl),
): Promise<EmailCodeResponse> {
  const body = {
    uid: input.uid,
    ...(input.email ? { email: input.email } : {}),
    ...(input.challenge_id ? { challenge_id: input.challenge_id } : {}),
    ...(input.conversationId ? { conversation_id: input.conversationId } : {}),
    ...(input.code ? { code: input.code } : {}),
  };
  if (input.operation === "playground") {
    const result = await postAuth(handshakeUrl, "email", body, fetchImpl);
    return result.status === "ok" && result.auth.email_code === "verified"
      ? { status: "verified", retry_after: 0, tries_left: 0 }
      : emailCodeFailure;
  }
  try {
    const result = await fetchImpl(
      proxyUrl(handshakeUrl, `email-code/${input.operation}`),
      {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(25_000),
      },
    );
    const parsed = emailCodeResponseSchema.safeParse(await result.json());
    return result.ok && parsed.success ? parsed.data : emailCodeFailure;
  } catch {
    return emailCodeFailure;
  }
}
